import { ValidationType } from "@/types/ValidationType";
import { minNumericValue } from "@/utils/numberUtils";
import { isCreditNoteAmount } from "@/modules/invoices/utils/invoice-document-type.utils";

export const CREDIT_NOTE_AMOUNT_MAX = -0.01;
export const NON_ZERO_AMOUNT_VALIDATION_MESSAGE = "El importe no puede ser cero";
const ALLOCATION_EXCEEDS_TOTAL_MESSAGE = "El importe en certificación no puede superar el importe neto de entrega";

export function parseDeliveryAmount(value: unknown): number | null {
  if (value instanceof Date || value === "" || value == null) {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
}

export function resolveInvoiceDeliveryAmount(amount: unknown, legacyTotalAmount?: unknown): number | null {
  return parseDeliveryAmount(amount) ?? parseDeliveryAmount(legacyTotalAmount);
}

export function resolveDeliveryAllocatedAmount(deliveryAmount: unknown, allocatedAmount: unknown): number | null {
  return parseDeliveryAmount(allocatedAmount) ?? parseDeliveryAmount(deliveryAmount);
}

export function createDeliveryAmountSignValidation(): ValidationType {
  return async (val: string | number | Date) => {
    const amount = parseDeliveryAmount(val);

    if (amount == null) {
      return true;
    }

    if (amount === 0) {
      return NON_ZERO_AMOUNT_VALIDATION_MESSAGE;
    }

    if (isCreditNoteAmount(amount)) {
      if (amount > CREDIT_NOTE_AMOUNT_MAX) {
        return "El importe del abono debe ser negativo";
      }

      if (amount < minNumericValue) {
        return `El valor no puede ser menor que ${minNumericValue}`;
      }

      return true;
    }

    if (amount < 0) {
      return "El importe debe ser mayor que cero";
    }

    return true;
  };
}

export function validateDeliveryAmountAllocation(deliveryAmount: unknown, allocatedAmount: unknown): string | null {
  const parsedDeliveryAmount = parseDeliveryAmount(deliveryAmount);
  const parsedAllocatedAmount = resolveDeliveryAllocatedAmount(deliveryAmount, allocatedAmount);

  if (parsedDeliveryAmount == null || parsedAllocatedAmount == null) {
    return null;
  }

  if (parsedDeliveryAmount === 0 || parsedAllocatedAmount === 0) {
    return NON_ZERO_AMOUNT_VALIDATION_MESSAGE;
  }

  const isCreditNote = isCreditNoteAmount(parsedDeliveryAmount);

  if (isCreditNote !== isCreditNoteAmount(parsedAllocatedAmount)) {
    return "El importe en certificación debe tener el mismo signo que el importe neto de entrega";
  }

  if (Math.abs(parsedAllocatedAmount) > Math.abs(parsedDeliveryAmount)) {
    return ALLOCATION_EXCEEDS_TOTAL_MESSAGE;
  }

  return null;
}

export function shouldSyncDeliveryAllocatedAmount(
  allocatedAmount: unknown,
  defaultDeliveryAmount: number | null,
  lastSyncedDeliveryAmount: unknown
): boolean {
  if (allocatedAmount == null || allocatedAmount === "") {
    return true;
  }

  if (defaultDeliveryAmount != null && allocatedAmount === defaultDeliveryAmount) {
    return true;
  }

  return allocatedAmount === lastSyncedDeliveryAmount;
}
